import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    IconChevronLeft, IconChevronRight, IconDownload, IconTrash, IconPrinter,
    IconSparkles, IconShare, IconCopy, IconPlus,
    IconLayoutColumns, IconColumns2, IconMinus, IconRefresh, IconWand, IconX, IconCheck,
} from '@tabler/icons-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import ConfirmDialog from './ConfirmDialog';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
    DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { notifications } from '@/lib/notifications';
import { useMediaQuery } from '@/lib/hooks';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import imageGenerationService from '../../services/imageGenerationService';
import { shareImageToTarget, copyImageToClipboard } from '../../utils/imageShare';
import { bakeImageAdjustments, fetchImageBlob } from '../../utils/bakeImageAdjustments';
import { markPrivateResultDelivered } from '../../utils/privateResults';
import { isSpecialBgToken, specialBgCss, hasTransparentSubject, compositeBackground, BG_HEX } from '../../pages/user/generate-image/bgPresets';

function getImageBgStyle(image) {
    // Fires for manual-editor outputs AND AI results with a transparent subject (.png).
    if (!hasTransparentSubject(image) || !image?.background) return {};
    const bg = image.background;
    if (bg === 'transparent') return {
        background: 'repeating-conic-gradient(#aaa 0% 25%,#e8e8e8 0% 50%) 0 0/20px 20px',
    };
    // gradient: / pattern: / preset: tokens
    if (isSpecialBgToken(bg)) return specialBgCss(bg) || {};
    const hex = BG_HEX[bg] || (bg.startsWith('#') || bg.startsWith('rgb') ? bg : null);
    if (!hex) return {};
    return { background: hex };
}

/* compositeBackground + BG_HEX now live in bgPresets (shared with /documents). */

/* ── Small reusable icon-button with Radix tooltip ──────────────────────────────
   Pass `label` to render an icon + text pill (used by the desktop action bar so
   each control is self-explanatory); omit it for a compact icon-only button. */
function IconBtn({ onClick, title, disabled, loading, label, className = '', children }) {
    const btn = (
        <button
            type="button"
            disabled={disabled || loading}
            onClick={onClick}
            className={`${label ? 'h-8 px-2.5 gap-1.5 text-[13px] font-semibold' : 'p-1.5'} rounded-lg inline-flex items-center justify-center whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
        >
            {loading ? (
                <svg className="animate-spin h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
            ) : children}
            {label && <span>{label}</span>}
        </button>
    );

    if (!title) return btn;

    return (
        <Tooltip>
            <TooltipTrigger asChild>{btn}</TooltipTrigger>
            <TooltipContent side="bottom">{title}</TooltipContent>
        </Tooltip>
    );
}

/* ── Zoom controls overlay ───────────────────────────────────────────────────── */
// One half of the phone's compare view. The photo fits its pane, so a background on the <img> fills only the photo, as in the desktop panes.
function PhoneComparePane({ src, alt, label, imgStyle }) {
    return (
        <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center">
                <img src={src} alt={alt} draggable={false} className="block max-h-full max-w-full" style={{ userSelect: 'none', pointerEvents: 'none', ...imgStyle }} />
            </div>
            <span className="pointer-events-none absolute left-1/2 top-2 z-[2] -translate-x-1/2 whitespace-nowrap rounded-md bg-black/60 px-2.5 py-0.5 !text-xs font-bold !text-white">{label}</span>
        </div>
    );
}

function ZoomControls({ zoom, setZoom, setPan, visible, t }) {
    return (
        <div
            style={{
                opacity: visible ? 1 : 0,
                pointerEvents: visible ? 'auto' : 'none',
                transition: 'opacity 0.2s ease',
                position: 'absolute',
                bottom: 12,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 5,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                background: 'rgba(0,0,0,0.65)',
                borderRadius: 24,
                padding: '5px 10px',
                backdropFilter: 'blur(6px)',
            }}
            onPointerDown={e => e.stopPropagation()}
        >
            <IconBtn
                title={t('imageGeneration.tooltipZoomOut')}
                disabled={zoom <= 1}
                onClick={() => setZoom(z => Math.max(1, z - 0.25))}
                className="text-white hover:bg-white/20"
            >
                <IconMinus size={14} />
            </IconBtn>
            <span style={{ minWidth: 38, textAlign: 'center', fontSize: 12, fontWeight: 700, color: 'white' }}>
                {Math.round(zoom * 100)}%
            </span>
            <IconBtn
                title={t('imageGeneration.tooltipZoomIn')}
                disabled={zoom >= 4}
                onClick={() => setZoom(z => Math.min(4, z + 0.25))}
                className="text-white hover:bg-white/20"
            >
                <IconPlus size={14} />
            </IconBtn>
            <IconBtn
                title={t('imageGeneration.tooltipResetZoom')}
                disabled={zoom === 1}
                onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
                className="text-orange-400 hover:bg-white/20"
            >
                <IconRefresh size={14} />
            </IconBtn>
        </div>
    );
}

/* ── Draggable image pane hook ───────────────────────────────────────────────── */
// Pan limits: the PAINTED image (object-fit: contain shrinks it inside its box)
// scaled by `zoom` must always cover the pane up to its own edge — dragging
// stops when an image edge reaches the pane edge, and an axis where the image
// fits entirely inside the pane cannot be panned at all.
function panBounds(container, zoom) {
    const img = container?.querySelector('img');
    if (!img) return null;
    let iw = img.offsetWidth;
    let ih = img.offsetHeight;
    if (img.naturalWidth && img.naturalHeight && iw && ih) {
        const natural = img.naturalWidth / img.naturalHeight;
        if (iw / ih > natural) iw = ih * natural; // letterboxed left/right
        else ih = iw / natural;                   // letterboxed top/bottom
    }
    return {
        x: Math.max(0, (iw * zoom - container.clientWidth) / 2),
        y: Math.max(0, (ih * zoom - container.clientHeight) / 2),
    };
}

const clampPan = (v, max) => Math.min(max, Math.max(-max, v));

function useDraggable(zoom, pan, setPan) {
    const dragRef = useRef(null);
    const containerRef = useRef(null); // the pane div — set via ref on the element

    const onPointerDown = useCallback((e) => {
        if (zoom <= 1) return;
        e.preventDefault(); // stop the browser from starting a text selection on drag
        containerRef.current = e.currentTarget;
        e.currentTarget.setPointerCapture(e.pointerId);
        dragRef.current = {
            startX: e.clientX, startY: e.clientY, startPan: { ...pan },
            bounds: panBounds(e.currentTarget, zoom),
        };
    }, [zoom, pan]);

    const onPointerMove = useCallback((e) => {
        if (!dragRef.current) return;
        const { startX, startY, startPan, bounds } = dragRef.current;
        let x = startPan.x + (e.clientX - startX);
        let y = startPan.y + (e.clientY - startY);
        if (bounds) { x = clampPan(x, bounds.x); y = clampPan(y, bounds.y); }
        setPan({ x, y });
    }, [setPan]);

    const onPointerUp = useCallback(() => { dragRef.current = null; }, []);

    // Zooming out shrinks the bounds — pull an out-of-range pan back inside.
    useEffect(() => {
        const bounds = panBounds(containerRef.current, zoom);
        if (!bounds) return;
        setPan(p => {
            const x = clampPan(p.x, bounds.x);
            const y = clampPan(p.y, bounds.y);
            return x === p.x && y === p.y ? p : { x, y };
        });
    }, [zoom, setPan]);

    return { dragRef, containerRef, onPointerDown, onPointerMove, onPointerUp };
}

/* ═══════════════════════════════════════════════════════════════════════════════
   Main component
   ═══════════════════════════════════════════════════════════════════════════════ */
export default function ImagePreviewModal({
    opened,
    onClose,
    images,
    initialImage,
    onImageDeleted,
    onPrint,
    onCustomDownload,
    hasMore = false,
    isLoadingMore = false,
    onLoadMore,
    isLoadingEdit = false,
    onEdit,
    onDownloadImage,
    onDelete = null, // (img) => Promise — custom deleter for images not owned by imageGenerationService (e.g. bulk projects)
    onGetShareBlob = null, // (img) => Promise<Blob> — share-blob source for images not owned by imageGenerationService
    initialAdjustments,
    hidePrint = false,
    hideEdit = false,
    hideShare = false,
    hideDownload = false,
    hideCustomDownload = false,
    // Delete talks to the image-generation API, so tools whose images only live
    // in the browser hide it without losing the before/after compare that
    // `minimalActions` would also switch off.
    hideDelete = false,
    // 'side-by-side' opens straight into the before/after panes — for tools whose
    // whole reason to open the viewer is the comparison.
    initialViewMode = 'single',
    minimalActions = false,
    title, // optional header title override (e.g. a style name); falls back to the generic label
    useImageTitle = false, // when true, the header shows the CURRENT image's fileName (updates on prev/next)
    onMakeActive = null, // (imageId) => void — shows a "select/make active" button in the header
    activeId = null, // id of the currently-active item → button shows a "selected" state
    makeActiveLabel = 'Select', // label for the make-active button
    selectedLabel = 'Selected', // label when the current item is already active
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width: 768px)');

    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [printing, setPrinting] = useState(false);

    const [viewMode, setViewMode] = useState('single');
    const [imgAspectRatio, setImgAspectRatio] = useState(null);
    // Tracked so the modal re-fits itself when the window is resized.
    const [winSize, setWinSize] = useState(() => (
        typeof window !== 'undefined' ? { w: window.innerWidth, h: window.innerHeight } : { w: 900, h: 860 }
    ));

    const [afterZoom, setAfterZoom] = useState(1);
    const [afterPan, setAfterPan] = useState({ x: 0, y: 0 });
    const [afterHover, setAfterHover] = useState(false);

    const [beforeZoom, setBeforeZoom] = useState(1);
    const [beforePan, setBeforePan] = useState({ x: 0, y: 0 });
    const [beforeHover, setBeforeHover] = useState(false);

    // The floating toolbar stays hidden and only reveals while the cursor is over the
    // PHOTO itself (the image frame / compare panes) — not the dark margins — so it
    // never obstructs the image. A short close delay bridges the gap between the photo
    // and the toolbar (separate elements floating just above it) so it doesn't flicker.
    const [chromeVisible, setChromeVisible] = useState(false);
    const chromeTimer = useRef(null);
    const showChrome = useCallback(() => { clearTimeout(chromeTimer.current); setChromeVisible(true); }, []);
    const hideChrome = useCallback(() => {
        clearTimeout(chromeTimer.current);
        chromeTimer.current = setTimeout(() => setChromeVisible(false), 260);
    }, []);

    const [showEditSidebar, setShowEditSidebar] = useState(false);
    const [brightness, setBrightness] = useState(100);
    const [contrast, setContrast] = useState(100);
    const [saturation, setSaturation] = useState(100);
    const [grayscale, setGrayscale] = useState(0);
    const [sharp, setSharp] = useState(0);
    const hasAdjustmentsChanged = brightness !== 100 || contrast !== 100 || saturation !== 100 || grayscale !== 0 || sharp !== 0;

    const snapTimers = useRef({});
    const withSnap = (setter, neutral) => (v) => {
        const numV = Number(v);
        if (Math.abs(numV - neutral) <= 6) {
            setter(neutral);
            clearTimeout(snapTimers.current[neutral]);
            snapTimers.current[neutral] = setTimeout(() => { delete snapTimers.current[neutral]; }, 150);
        } else if (!snapTimers.current[neutral]) {
            setter(numV);
        }
    };

    const pendingLoadMoreRef = useRef(false);
    const prevImagesLengthRef = useRef(0);
    // Pending saved pan (fractional) to apply after image+container are measured
    const pendingPanRef = useRef(null);

    // Compare view: zoom AND pan are SYNCED across both panes — zooming, resetting,
    // or DRAGGING either side drives BOTH panes together so before/after stay
    // aligned (pan bounds are still measured from the pane actually being dragged).
    const setCompareZoom = (updater) => {
        setBeforeZoom(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            setAfterZoom(next);
            return next;
        });
    };
    const setComparePan = useCallback((val) => { setBeforePan(val); setAfterPan(val); }, []);

    // Draggable hooks. Single + mobile pan each pane independently; in compare view
    // both the before pane (beforeDrag) and the after pane (afterCompareDrag) drive
    // setComparePan so a drag on either side moves BOTH images in lockstep.
    const afterDrag = useDraggable(afterZoom, afterPan, setAfterPan);
    const afterCompareDrag = useDraggable(afterZoom, afterPan, setComparePan);
    const beforeDrag = useDraggable(beforeZoom, beforePan, setComparePan);

    useEffect(() => {
        if (opened && initialImage && images?.length > 0) {
            const index = images.findIndex(img => img.id === initialImage.id);
            setCurrentIndex(index >= 0 ? index : 0);
            setSelectedImage(initialImage);
            setViewMode(initialImage.uploadedImage ? initialViewMode : 'single');
            setImgAspectRatio(null);
            const initZoom = initialImage.zoom ?? 1;
            setAfterZoom(initZoom); setAfterPan({ x: 0, y: 0 });
            setBeforeZoom(1); setBeforePan({ x: 0, y: 0 });
            // If zoom+offset saved, apply pan after image loads (container size needed)
            if (initZoom !== 1 && (initialImage.offsetX || initialImage.offsetY)) {
                pendingPanRef.current = { ox: initialImage.offsetX ?? 0, oy: initialImage.offsetY ?? 0 };
            } else {
                pendingPanRef.current = null;
            }
            setShowEditSidebar(false);
            setChromeVisible(false);
            setBrightness(initialAdjustments?.brightness ?? 100);
            setContrast(initialAdjustments?.contrast ?? 100);
            setSaturation(initialAdjustments?.saturation ?? 100);
            setGrayscale(initialAdjustments?.grayscale ?? 0);
            setSharp(initialAdjustments?.sharp ?? 0);
            prevImagesLengthRef.current = images.length;
        }
    }, [opened, initialImage]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!opened) return;
        if (pendingLoadMoreRef.current && images.length > prevImagesLengthRef.current) {
            pendingLoadMoreRef.current = false;
            const newIndex = prevImagesLengthRef.current;
            const img = images[newIndex];
            setCurrentIndex(newIndex);
            setSelectedImage(img);
            setViewMode(prev => prev === 'side-by-side' && !img?.uploadedImage ? 'single' : prev);
            resetZoom(img);
        }
        prevImagesLengthRef.current = images?.length ?? 0;
    }, [images?.length, opened]);

    const resetZoom = useCallback((img) => {
        const initZoom = img?.zoom ?? 1;
        setAfterZoom(initZoom); setAfterPan({ x: 0, y: 0 });
        setBeforeZoom(1); setBeforePan({ x: 0, y: 0 });
        if (initZoom !== 1 && (img?.offsetX || img?.offsetY)) {
            pendingPanRef.current = { ox: img.offsetX ?? 0, oy: img.offsetY ?? 0 };
        } else {
            pendingPanRef.current = null;
        }
    }, []);

    const handlePrevImage = useCallback(() => {
        if (currentIndex > 0) {
            const newIndex = currentIndex - 1;
            const img = images[newIndex];
            setCurrentIndex(newIndex); setSelectedImage(img);
            setViewMode(prev => prev === 'side-by-side' && !img?.uploadedImage ? 'single' : prev);
            setImgAspectRatio(null);
            resetZoom(img);
        }
    }, [currentIndex, images, resetZoom]);

    const handleNextImage = useCallback(() => {
        if (currentIndex < images.length - 1) {
            const newIndex = currentIndex + 1;
            const img = images[newIndex];
            setCurrentIndex(newIndex); setSelectedImage(img);
            setViewMode(prev => prev === 'side-by-side' && !img?.uploadedImage ? 'single' : prev);
            setImgAspectRatio(null);
            resetZoom(img);
        } else if (hasMore && !isLoadingMore && onLoadMore) {
            pendingLoadMoreRef.current = true;
            onLoadMore();
        }
    }, [currentIndex, images, hasMore, isLoadingMore, onLoadMore, resetZoom]);

    useEffect(() => {
        if (!opened) return;
        const onKey = (e) => {
            if (e.key === 'ArrowLeft') handlePrevImage();
            else if (e.key === 'ArrowRight') handleNextImage();
            else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [opened, handlePrevImage, handleNextImage, onClose]);

    // Lock background page scroll while the modal is open. The app scrolls on an inner
    // container (not <body>), so instead of toggling body overflow we block wheel/touch
    // scrolling outside the dialog — scrolling inside it (e.g. the adjustments sidebar)
    // still works because those targets are within [role="dialog"].
    useEffect(() => {
        if (!opened) return;
        const block = (e) => {
            if (!e.target?.closest?.('[role="dialog"]')) e.preventDefault();
        };
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('wheel', block, { passive: false });
        document.addEventListener('touchmove', block, { passive: false });
        return () => {
            document.body.style.overflow = prevOverflow;
            document.removeEventListener('wheel', block);
            document.removeEventListener('touchmove', block);
        };
    }, [opened]);

    useEffect(() => {
        if (!opened) return;
        const check = () => {
            setWinSize(prev => (prev.w === window.innerWidth && prev.h === window.innerHeight)
                ? prev : { w: window.innerWidth, h: window.innerHeight });
        };
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, [opened]);

    // The bytes of the current image — what a share, copy, print or download sends. Local results (blob:/data: URLs: the tool
    // pages) are read directly; callers whose images live outside imageGenerationService (bulk photo edit) supply onGetShareBlob;
    // anything else comes through the backend (the R2 host sends no CORS headers) with the background composited back in for
    // manually-edited images.
    const getShareBlob = async () => {
        const url = selectedImage.aiImage;
        if (typeof url === 'string' && /^(blob|data):/.test(url)) return (await fetch(url)).blob();
        if (onGetShareBlob) return onGetShareBlob(selectedImage);
        if (onDownloadImage) return fetchImageBlob(url); // downloads on its own but gave us no byte source
        const blob = await imageGenerationService.downloadImage(selectedImage.id);
        return hasTransparentSubject(selectedImage) ? compositeBackground(blob, selectedImage.background) : blob;
    };

    // The sidebar's edits are only a CSS filter on screen, so whatever leaves the modal has to be redrawn with them burnt in
    // (bakeImageAdjustments throws if it can't — the unedited file is never handed over in silence).
    const sidebarEdits = { brightness, contrast, saturation, grayscale, sharp };
    const getOutgoingBlob = async () => {
        const source = await getShareBlob();
        return hasAdjustmentsChanged ? bakeImageAdjustments(source, sidebarEdits) : source;
    };

    // Share the ACTUAL image, never its URL: native share sheet with the file
    // attached where available, else copy the image to the clipboard (and open
    // WhatsApp Web / Gmail for those targets) so the user pastes it in.
    const handleShare = async (type) => {
        if (!selectedImage) return;
        try {
            const shareTitle = t('imageGeneration.shareTitle') || 'Generated Image';
            if (type === 'copy') {
                await copyImageToClipboard(getOutgoingBlob());
                markPrivateResultDelivered(selectedImage.id);
                notifications.show({ title: t('imageGeneration.imageCopied'), message: t('imageGeneration.imageCopiedPasteHint'), color: 'green', autoClose: 7000 });
                return;
            }
            const result = await shareImageToTarget(getOutgoingBlob, type, {
                filename: `${selectedImage.fileName || 'sohozkaj-image'}.jpg`,
                title: shareTitle,
            });
            markPrivateResultDelivered(selectedImage.id);
            if (result === 'copied') {
                notifications.show({ title: t('imageGeneration.imageCopied'), message: t('imageGeneration.imageCopiedPasteHint'), color: 'green', autoClose: 7000 });
            }
        } catch (err) {
            if (err?.name === 'AbortError') return; // user closed the native share sheet
            notifications.show({ title: t('common.error'), message: t('documents.shareFailed'), color: 'red' });
        }
    };

    const handlePrint = async () => {
        if (!selectedImage) return;
        setPrinting(true);
        try {
            if (!hasAdjustmentsChanged) { onPrint?.(selectedImage.aiImage, selectedImage.id); if (onPrint) onClose?.(); return; }
            // An edited picture prints as the redrawn copy (a blob: URL), not the stored original.
            const edited = await bakeImageAdjustments(await getShareBlob(), sidebarEdits);
            onPrint?.(URL.createObjectURL(edited), selectedImage.id);
            if (onPrint) onClose?.();
        } catch {
            notifications.show({ title: t('common.error'), message: t('imageGeneration.downloadFailed'), color: 'red' });
        } finally { setPrinting(false); }
    };

    const handleDownload = async () => {
        if (!selectedImage) return;
        setDownloading(true);
        try {
            // An edited picture is redrawn with the sidebar's edits and saved here rather than handed to the
            // caller: every `onDownloadImage` fetches its own copy of the ORIGINAL, so routing through one
            // would silently drop the edits.
            if (hasAdjustmentsChanged) {
                const adjusted = await bakeImageAdjustments(await getShareBlob(), sidebarEdits);
                const ext = (adjusted.type || '').includes('png') ? 'png'
                    : (adjusted.type || '').includes('webp') ? 'webp' : 'jpg';
                const base = (selectedImage.fileName || 'sohozkaj-image').replace(/\.[^.]+$/, '');
                const url = URL.createObjectURL(adjusted);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${base}-edited.${ext}`;
                document.body.appendChild(link); link.click(); document.body.removeChild(link);
                setTimeout(() => URL.revokeObjectURL(url), 1000);
                notifications.show({ title: t('common.success'), message: t('imageGeneration.imageDownloaded'), color: 'green' });
                if (!onDownloadImage) imageGenerationService.trackDownload(selectedImage.id).catch(() => {});
                return;
            }

            // Caller-supplied download (e.g. images not owned by imageGenerationService).
            if (onDownloadImage) { await onDownloadImage(selectedImage); return; }
            const blob = await imageGenerationService.downloadImage(selectedImage.id);
            // Transparent-subject images (manual-editor outputs AND AI transparent
            // results) are stored with a transparent subject and the background kept as
            // a token (solid / gradient / pattern / preset). Composite it back in so the
            // download matches the preview.
            const finalBlob = hasTransparentSubject(selectedImage)
                ? await compositeBackground(blob, selectedImage.background)
                : blob;

            const url = URL.createObjectURL(finalBlob);
            const link = document.createElement('a');
            link.href = url;
            const uuid = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2, 10);
            // compositeBackground returns PNG for kept-transparent results — name by the
            // real blob type so the alpha isn't mislabelled as .jpg.
            const ext = (finalBlob.type || '').includes('png') ? 'png'
                : (finalBlob.type || '').includes('webp') ? 'webp' : 'jpg';
            link.download = `sohozkaj-image-${uuid}+${Date.now()}.${ext}`;
            document.body.appendChild(link); link.click(); document.body.removeChild(link);
            URL.revokeObjectURL(url);
            notifications.show({ title: t('common.success'), message: t('imageGeneration.imageDownloaded'), color: 'green' });
            imageGenerationService.trackDownload(selectedImage.id).catch(() => {});
        } catch { notifications.show({ title: t('common.error'), message: t('imageGeneration.downloadFailed'), color: 'red' }); }
        finally { setDownloading(false); }
    };

    const handleDelete = async () => {
        if (!selectedImage) return;
        setDeleting(true);
        try {
            if (onDelete) await onDelete(selectedImage);
            else await imageGenerationService.deleteImage(selectedImage.id);
            notifications.show({ title: t('common.success'), message: t('imageGeneration.imageDeleted') || 'Image deleted', color: 'green' });
            setShowDeleteConfirm(false); onClose(); onImageDeleted?.(selectedImage.id);
        } catch { notifications.show({ title: t('common.error'), message: t('imageGeneration.deleteFailed') || 'Delete failed', color: 'red' }); }
        finally { setDeleting(false); }
    };

    const handleEdit = () => {
        if (!selectedImage) return;
        if (onEdit) { onEdit(selectedImage); return; }
        onClose();
        if (selectedImage.manuallyEdited === true) {
            // Re-open with full metadata → the manual editor restores it straight into the
            // canvas (fromHistory path). Passing only imageId/imageUrl would be treated as a
            // fresh upload and would pop the crop modal first, which we don't want here.
            navigate('/generate-image-manual', {
                state: {
                    historyItem: {
                        id: selectedImage.id,
                        aiImage: selectedImage.aiImage,
                        background: selectedImage.background,
                        size: selectedImage.size || selectedImage.photoSize,
                        zoom: selectedImage.zoom ?? null,
                        offsetX: selectedImage.offsetX ?? null,
                        offsetY: selectedImage.offsetY ?? null,
                        manualBgRemoved: selectedImage.manualBgRemoved ?? false,
                        fileName: selectedImage.fileName,
                    },
                },
            });
        } else if (selectedImage.studioEdited === true) {
            navigate(`/studio-editor?editId=${selectedImage.id}`);
        } else if (selectedImage.aiTemplateGeneration === true) {
            const templateId = selectedImage.aiTemplateId || selectedImage.metadata?.aiTemplateId;
            if (templateId) {
                // the template page opens THIS photo as its result (edit drawer, download, print…), not just the template
                navigate(`/ai-templates/${templateId}?editId=${encodeURIComponent(selectedImage.id)}`);
            } else {
                navigate('/ai-templates');
            }
        } else {
            navigate(`/generate-image?editId=${selectedImage.id}`);
        }
    };

    const cssFilter = hasAdjustmentsChanged
        ? `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) grayscale(${grayscale}%)${sharp > 0 ? ' url(#ipm-sharpen)' : ''}`
        : undefined;
    // (A Studio Editor photo of two people also stores "a|b" in dressCode, but it is ONE ordinary photo — only the passport Dual flow merges two.)
    const isDual = selectedImage?.photoSize === 'dual_image' || (!!selectedImage?.dressCode?.includes('|') && !selectedImage?.studioEdited);
    const hasNextImage = currentIndex < (images?.length ?? 0) - 1 || hasMore;

    if (!selectedImage) return null;

    // Modal sizing — the toolbar floats OVER the image (absolute, transparent) so the
    // photo gets the FULL viewport height, edge-to-edge. Only the viewport width still
    // bounds it (0.9 so the dark backdrop shows on both sides; never stretched).
    const MAX_IMG_W = winSize.w * 0.9;
    const MAX_IMG_H = winSize.h;
    // Single-mode content width, derived from the image ratio + current window
    // size (recomputed on resize, so the modal re-fits live).
    const singleBaseW = imgAspectRatio
        ? Math.min(Math.round(MAX_IMG_H * imgAspectRatio), MAX_IMG_W)
        : 626;
    // Matching frame height (the image keeps its aspect ratio). The single-view frame is
    // sized to this box so zooming crops inside it instead of growing the photo.
    const singleBaseH = imgAspectRatio ? Math.round(singleBaseW / imgAspectRatio) : Math.round(MAX_IMG_H);

    // When the adjustments sidebar is open it occupies the right edge, so the image
    // re-centers into the remaining area. The toolbar must center over that SAME area
    // (not the full viewport) or it drifts right of the photo.
    const SIDEBAR_W = 260;
    const sidebarOpen = showEditSidebar;
    const bodyW = winSize.w - (sidebarOpen ? SIDEBAR_W : 0);

    // Where the floating toolbar sits, and how much width it has to work with:
    //  • single       → hugs the displayed image width, centered above the photo
    //  • split compare → floats over the RIGHT (after) pane only
    //  • dual compare  → spans the modal (panes are stacked, so no right pane)
    const isSplitCompare = viewMode === 'side-by-side' && !isDual;
    const toolbarWidth = viewMode === 'single'
        ? singleBaseW
        : (isSplitCompare ? bodyW / 2 : bodyW);
    const headerPosStyle = viewMode === 'single'
        ? { left: 0, right: sidebarOpen ? SIDEBAR_W : 0, width: singleBaseW, marginInline: 'auto' }
        : (isSplitCompare
            ? { left: bodyW / 2, right: sidebarOpen ? SIDEBAR_W : 0 }
            : { left: 0, right: sidebarOpen ? SIDEBAR_W : 0 });

    // Progressive label hiding: rather than dropping every label at once when the bar
    // gets narrow, keep the most-used actions labelled (download → print → …) and
    // collapse the rest to icon-only one at a time by priority as space runs out.
    // Widths are conservative px estimates of each labelled pill (icon + text) so the
    // row stays comfortably on one line; tooltips still name every action.
    const LABEL_PX = { download: 108, print: 84, edit: 104, share: 88, delete: 80, compare: 92, glow: 80 };
    const ICON_PX = 34; // icon-only pill (p-1.5) + inter-item gap
    const LABEL_PRIORITY = ['download', 'print', 'edit', 'share', 'delete', 'compare', 'glow'];
    const present = {
        compare: !minimalActions && !!selectedImage?.uploadedImage && !selectedImage?.aiTemplateGeneration,
        share: !hideShare,
        edit: !hideEdit,
        download: !hideDownload,
        print: !hidePrint,
        delete: !minimalActions,
        glow: !minimalActions,
    };
    const labeledSet = (() => {
        const set = new Set();
        // Base cost: header padding + close button + optional make-active button, plus
        // every present action as an icon. Then upgrade to labelled pills by priority.
        let used = 28 + ICON_PX + (onMakeActive && selectedImage ? 118 : 0);
        for (const k of LABEL_PRIORITY) if (present[k]) used += ICON_PX;
        for (const k of LABEL_PRIORITY) {
            if (!present[k]) continue;
            const extra = LABEL_PX[k] - ICON_PX;
            if (used + extra <= toolbarWidth) { set.add(k); used += extra; }
            else break; // stop at the first that doesn't fit → preserves priority order
        }
        return set;
    })();
    const showLabel = (k) => labeledSet.has(k);

    const modalStyle = isMobile ? {} : (() => {
        const isSingle = viewMode === 'single';
        if (!isSingle) {
            // Full viewport width so the header/toolbar stays full-width in compare mode
            // too; the before/after panes are transparent with each image centered.
            return {
                width: '100vw',
                height: '100vh',
            };
        }
        // Full viewport width so the header/toolbar is a full-width top bar. The modal
        // background is transparent (see DialogContent) so the image below just fits its
        // own size with the dark backdrop showing beside it — no container box.
        return {
            width: '100vw',
            height: '100vh',
            maxHeight: '100vh',
        };
    })();

    return (
        <>
        {/* Backdrop — portaled to <body> so its z-index escapes the page content's
            stacking context and covers the whole page (incl. the sticky topbar).
            Rendered independently because modal={false} suppresses Radix's own overlay. */}
        {opened && createPortal(
            <div
                onClick={onClose}
                className="sk-editor-stage"
                style={{ position: 'fixed', inset: 0, zIndex: 99998 }}
            />,
            document.body
        )}

        {/* Hidden SVG sharpen filter */}
        <svg style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
            <defs>
                <filter id="ipm-sharpen">
                    <feConvolveMatrix order="3" preserveAlpha="true"
                        kernelMatrix={`0 ${-(sharp/10).toFixed(3)} 0 ${-(sharp/10).toFixed(3)} ${(1+4*(sharp/10)).toFixed(3)} ${-(sharp/10).toFixed(3)} 0 ${-(sharp/10).toFixed(3)} 0`}
                    />
                </filter>
            </defs>
        </svg>

        <Dialog open={opened} onOpenChange={(v) => !v && onClose()} modal={false}>
            <DialogContent
                showClose={false}
                // Don't pull focus into the dialog on open — Radix otherwise focuses the
                // first control (the Compare pill), lighting up its focus ring + tooltip as
                // if pre-selected. The viewer is driven by mouse + global key handlers, so
                // leaving focus untouched loses nothing.
                onOpenAutoFocus={(e) => e.preventDefault()}
                // This is a non-modal dialog with its OWN backdrop (onClick → onClose),
                // so let Radix's auto-dismiss stay out of the way: don't close when focus
                // or a pointer lands outside. Without this, opening a nested dialog (the
                // delete confirmation) moved focus out and Radix closed this viewer —
                // which unmounted the confirmation before it could show. Close paths kept:
                // the × button, Escape, and the backdrop click.
                onInteractOutside={(e) => e.preventDefault()}
                onFocusOutside={(e) => e.preventDefault()}
                overlayClassName="z-[99998]"
                className={isMobile
                    ? 'max-w-none w-screen h-[100dvh] m-0 rounded-none p-0 flex flex-col bg-transparent dark:bg-transparent overflow-hidden z-[99999]'
                    : 'max-w-none p-0 rounded-none overflow-hidden flex flex-col z-[99999] bg-transparent dark:bg-transparent shadow-none'
                }
                style={isMobile ? {} : {
                    ...modalStyle,
                    transition: 'width 0.15s ease',
                    // Neutralise Radix's zoom/slide enter+exit transforms (they scale the
                    // content to 95% and shift it — read as the modal "shrinking" on close).
                    // The opacity fade is kept for a clean appear/disappear.
                    '--tw-enter-scale': 1,
                    '--tw-enter-translate-x': '0px',
                    '--tw-enter-translate-y': '0px',
                    // Exit-only: mirror the entrance (same scale + the base -50% centering
                    // translate). The exit keyframe REPLACES the base transform, so a 0px
                    // translate here made the viewer jump right/down while fading out.
                    '--tw-exit-scale': 0.9,
                    '--tw-exit-translate-x': '-50%',
                    '--tw-exit-translate-y': '-50%',
                }}
                aria-describedby={undefined}
            >
                <DialogTitle className="sr-only">{title || t('imageGeneration.generatedImage')}</DialogTitle>

                {/* ══════════════════════════════════════════════════
                    MOBILE — native app layout
                ══════════════════════════════════════════════════ */}
                {isMobile ? (
                    <div className="flex flex-col h-full" data-mantine-color-scheme="dark">

                        {/* ── Top bar ── */}
                        <div className="flex items-center gap-2 px-3 pt-3 pb-2 flex-shrink-0">
                            {/* Back / close */}
                            <button
                                type="button"
                                onClick={onClose}
                                className="w-9 h-9 rounded-full bg-zinc-100 dark:bg-white/10 flex items-center justify-center text-zinc-700 dark:text-white active:bg-zinc-200 dark:active:bg-white/20 transition-colors flex-shrink-0"
                            >
                                <IconChevronLeft size={22} strokeWidth={2.5} />
                            </button>

                            {/* Title (if provided) or counter pill, centered */}
                            <div className="flex-1 flex justify-center min-w-0 px-2">
                                {(title || (useImageTitle && selectedImage?.fileName)) ? (
                                    <span className="text-zinc-700 dark:text-white text-sm font-semibold truncate">
                                        {title || selectedImage?.fileName}
                                    </span>
                                ) : (images?.length > 1 || hasMore) && (
                                    <span className="text-zinc-500 dark:text-white/60 text-sm font-medium tabular-nums">
                                        {currentIndex + 1} / {images?.length ?? 0}
                                    </span>
                                )}
                            </div>

                            {/* Make-active / select-this-preset button */}
                            {onMakeActive && selectedImage && (
                                <button
                                    type="button"
                                    onClick={() => onMakeActive(selectedImage.id)}
                                    disabled={selectedImage.id === activeId}
                                    className={`flex-shrink-0 inline-flex items-center gap-1 h-9 px-3 rounded-full text-[13px] font-semibold transition-colors ${
                                        selectedImage.id === activeId
                                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 cursor-default'
                                            : 'bg-primary text-white active:bg-primary/90'
                                    }`}
                                >
                                    <IconCheck size={15} stroke={2.4} />
                                    {selectedImage.id === activeId ? selectedLabel : makeActiveLabel}
                                </button>
                            )}

                            {/* Compare */}
                            {present.compare && (
                                <button
                                    type="button"
                                    onClick={() => setViewMode(m => m === 'single' ? 'side-by-side' : 'single')}
                                    aria-pressed={viewMode === 'side-by-side'}
                                    aria-label={viewMode === 'single' ? t('imageGeneration.compareBeforeAfter') : t('imageGeneration.exitCompare')}
                                    className={`flex-shrink-0 inline-flex items-center gap-1 h-9 px-3 rounded-full text-[13px] font-semibold transition-colors ${
                                        viewMode === 'side-by-side'
                                            ? 'bg-primary text-white active:bg-primary/90'
                                            : 'bg-zinc-100 dark:bg-white/10 text-zinc-700 dark:text-white active:bg-zinc-200 dark:active:bg-white/20'
                                    }`}
                                >
                                    {viewMode === 'side-by-side' ? <IconColumns2 size={16} /> : <IconLayoutColumns size={16} />}
                                    <span className="!text-[13px]">{viewMode === 'single' ? t('imageGeneration.labelCompare') : t('imageGeneration.labelExit')}</span>
                                </button>
                            )}

                            {/* Share */}
                            {!hideShare && (
                            <DropdownMenu>
                                <DropdownMenuTrigger
                                    type="button"
                                    className="w-9 h-9 rounded-full bg-zinc-100 dark:bg-white/10 flex items-center justify-center text-zinc-700 dark:text-white active:bg-zinc-200 dark:active:bg-white/20 transition-colors flex-shrink-0"
                                >
                                    <IconShare size={17} />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-44 mt-2 rounded-xl overflow-hidden p-1 shadow-xl">
                                    <DropdownMenuLabel className="text-xs font-semibold px-2.5 py-1.5">{t('imageGeneration.shareImage')}</DropdownMenuLabel>
                                    <DropdownMenuSeparator className="my-0.5" />
                                    {navigator.share && (
                                        <>
                                            <DropdownMenuItem
                                                className="cursor-pointer rounded-lg px-2.5 py-2 flex items-center gap-2"
                                                onClick={() => handleShare('native')}
                                            >
                                                <div className="w-6 h-6 rounded-lg bg-violet-100 dark:bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                                                    <IconShare size={13} className="text-violet-500 dark:text-violet-400" />
                                                </div>
                                                <span className="text-xs font-medium">{t('imageGeneration.share')}...</span>
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator className="my-0.5" />
                                        </>
                                    )}
                                    <DropdownMenuItem
                                        className="cursor-pointer rounded-lg px-2.5 py-2 flex items-center gap-2"
                                        onClick={() => handleShare('copy')}
                                    >
                                        <div className="w-6 h-6 rounded-lg bg-zinc-100 dark:bg-white/10 flex items-center justify-center flex-shrink-0">
                                            <IconCopy size={13} className="text-zinc-600 dark:text-white" />
                                        </div>
                                        <span className="text-xs font-medium">{t('imageGeneration.copyImage')}</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            )}
                        </div>

                        {/* ── Image area ── */}
                        <div
                            ref={afterDrag.containerRef}
                            className="flex-1 flex items-center justify-center relative overflow-hidden"
                            style={{ cursor: afterZoom > 1 ? (afterDrag.dragRef.current ? 'grabbing' : 'grab') : 'default' }}
                            onPointerDown={afterDrag.onPointerDown}
                            onPointerMove={afterDrag.onPointerMove}
                            onPointerUp={afterDrag.onPointerUp}
                            onPointerCancel={afterDrag.onPointerUp}
                        >
                            {/* Compare: stacked photos, so each is bigger on a tall screen; side by side on a phone held sideways */}
                            {viewMode === 'side-by-side' && selectedImage?.uploadedImage ? (
                                <div className={`flex h-full w-full gap-1.5 ${isDual || winSize.h >= winSize.w ? 'flex-col' : 'flex-row'}`}>
                                    <PhoneComparePane src={selectedImage.uploadedImage} alt="Before" label={t('imageGeneration.beforeLabel')} />
                                    <PhoneComparePane
                                        src={selectedImage.aiImage}
                                        alt="After"
                                        label={t('imageGeneration.afterLabel')}
                                        imgStyle={{ filter: cssFilter, transform: `scale(${afterZoom}) translate(${afterPan.x / afterZoom}px, ${afterPan.y / afterZoom}px)`, ...getImageBgStyle(selectedImage) }}
                                    />
                                </div>
                            ) : (
                                /* Manual images keep their w-full frame; for everything else the wrapper
                                   is `contents` so the <img> is a direct flex child and its own background
                                   (set below) fills exactly the contained image — no letterbox bleed. */
                                <div
                                    className={selectedImage?.manuallyEdited ? 'w-full flex-shrink-0' : 'contents'}
                                    style={selectedImage?.manuallyEdited ? getImageBgStyle(selectedImage) : {}}
                                >
                                <img
                                    src={selectedImage.aiImage}
                                    alt="Preview"
                                    draggable={false}
                                    style={selectedImage?.manuallyEdited ? {
                                        width: '100%',
                                        height: 'auto',
                                        display: 'block',
                                        filter: cssFilter,
                                        transform: `scale(${afterZoom}) translate(${afterPan.x / afterZoom}px, ${afterPan.y / afterZoom}px)`,
                                        transformOrigin: 'center center',
                                        transition: afterDrag.dragRef.current ? 'none' : 'transform 0.15s ease',
                                        userSelect: 'none',
                                        pointerEvents: 'none',
                                    } : {
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        width: 'auto',
                                        height: 'auto',
                                        transform: `scale(${afterZoom}) translate(${afterPan.x / afterZoom}px, ${afterPan.y / afterZoom}px)`,
                                        transformOrigin: 'center center',
                                        transition: afterDrag.dragRef.current ? 'none' : 'transform 0.15s ease',
                                        filter: cssFilter,
                                        userSelect: 'none',
                                        ...getImageBgStyle(selectedImage),
                                        pointerEvents: 'none',
                                        display: 'block',
                                    }}
                                />
                                </div>
                            )}

                            {/* Prev / Next tap zones */}
                            {currentIndex > 0 && (
                                <button
                                    type="button"
                                    onClick={handlePrevImage}
                                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/20 dark:bg-black/40 flex items-center justify-center text-zinc-800 dark:text-white backdrop-blur-sm active:bg-black/30 dark:active:bg-black/60 transition-colors z-10"
                                >
                                    <IconChevronLeft size={22} />
                                </button>
                            )}
                            {hasNextImage && (
                                <button
                                    type="button"
                                    onClick={handleNextImage}
                                    disabled={pendingLoadMoreRef.current && isLoadingMore}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/20 dark:bg-black/40 flex items-center justify-center text-zinc-800 dark:text-white backdrop-blur-sm active:bg-black/30 dark:active:bg-black/60 transition-colors z-10"
                                >
                                    {pendingLoadMoreRef.current && isLoadingMore
                                        ? <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
                                        : <IconChevronRight size={22} />
                                    }
                                </button>
                            )}

                            {/* Zoom controls */}
                            <ZoomControls zoom={afterZoom} setZoom={setAfterZoom} setPan={setAfterPan} visible={afterHover} t={t} />
                        </div>

                        {/* ── Bottom action bar ── */}
                        <div className="flex-shrink-0 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl rounded-t-3xl pt-4 pb-6 px-4 border-t border-zinc-100 dark:border-white/5 shadow-[0_-4px_24px_rgba(0,0,0,0.08)] dark:shadow-none">
                            {/* Action tiles */}
                            <div className="flex items-start justify-evenly gap-2">

                                {/* Download */}
                                {!hideDownload && (hideCustomDownload ? (
                                    <button
                                        type="button"
                                        onClick={handleDownload}
                                        disabled={downloading}
                                        className="flex flex-col items-center gap-1.5 text-zinc-700 dark:text-white/80 active:opacity-70 transition-opacity disabled:opacity-40 w-16"
                                    >
                                        <div className="w-12 h-12 rounded-2xl bg-zinc-100 dark:bg-white/10 flex items-center justify-center">
                                            {downloading
                                                ? <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
                                                : <IconDownload size={22} />
                                            }
                                        </div>
                                        <span className="text-[10px] font-medium text-zinc-500 dark:text-white/60 text-center w-full leading-tight">{t('imageGeneration.tooltipDownload')}</span>
                                    </button>
                                ) : (
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <button
                                                type="button"
                                                disabled={downloading}
                                                className="flex flex-col items-center gap-1.5 text-zinc-700 dark:text-white/80 active:opacity-70 transition-opacity disabled:opacity-40 w-16"
                                            >
                                                <div className="w-12 h-12 rounded-2xl bg-zinc-100 dark:bg-white/10 flex items-center justify-center">
                                                    {downloading
                                                        ? <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
                                                        : <IconDownload size={22} />
                                                    }
                                                </div>
                                                <span className="text-[10px] font-medium text-zinc-500 dark:text-white/60 text-center w-full leading-tight">{t('imageGeneration.tooltipDownload')}</span>
                                            </button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="center" side="top" className="w-44 mb-3 rounded-xl overflow-hidden p-1 shadow-xl">
                                            <DropdownMenuLabel className="text-xs font-semibold px-2.5 py-1.5">{t('imageGeneration.downloadOptions')}</DropdownMenuLabel>
                                            <DropdownMenuSeparator className="my-0.5" />
                                            <DropdownMenuItem
                                                className="cursor-pointer rounded-lg px-2.5 py-2 flex items-center gap-2"
                                                onClick={handleDownload}
                                            >
                                                <div className="w-6 h-6 rounded-lg bg-zinc-100 dark:bg-white/10 flex items-center justify-center flex-shrink-0">
                                                    <IconDownload size={13} className="text-zinc-600 dark:text-white" />
                                                </div>
                                                <span className="text-xs font-medium">{t('imageGeneration.downloadOriginal')}</span>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                                className="cursor-pointer rounded-lg px-2.5 py-2 flex items-center gap-2"
                                                onClick={() => onCustomDownload?.(selectedImage.aiImage, selectedImage.id, selectedImage.background)}
                                            >
                                                <div className="w-6 h-6 rounded-lg bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                                                    <IconPlus size={13} className="text-orange-500 dark:text-orange-400" />
                                                </div>
                                                <span className="text-xs font-medium">{t('imageGeneration.customSizeQuality')}</span>
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                ))}

                                {/* Edit / Re-generate */}
                                {!hideEdit && (
                                    <button
                                        type="button"
                                        onClick={handleEdit}
                                        disabled={isLoadingEdit}
                                        className="flex flex-col items-center gap-1.5 active:opacity-70 transition-opacity disabled:opacity-40 w-16"
                                    >
                                        <div className="w-12 h-12 rounded-2xl bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center">
                                            {isLoadingEdit
                                                ? <svg className="animate-spin h-5 w-5 text-orange-500" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
                                                : <IconSparkles size={22} className="text-orange-500 dark:text-orange-400" />
                                            }
                                        </div>
                                        <span className="text-[10px] font-medium text-zinc-500 dark:text-white/60 text-center w-full leading-tight">{t('imageGeneration.tooltipReopenEditor')}</span>
                                    </button>
                                )}

                                {/* Print */}
                                {!hidePrint && (
                                    <button
                                        type="button"
                                        onClick={handlePrint}
                                        disabled={printing}
                                        className="flex flex-col items-center gap-1.5 active:opacity-70 transition-opacity disabled:opacity-40 w-16"
                                    >
                                        <div className="w-12 h-12 rounded-2xl bg-cyan-100 dark:bg-cyan-500/20 flex items-center justify-center">
                                            {printing
                                                ? <svg className="animate-spin h-5 w-5 text-cyan-500" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
                                                : <IconPrinter size={22} className="text-cyan-600 dark:text-cyan-400" />
                                            }
                                        </div>
                                        <span className="text-[10px] font-medium text-zinc-500 dark:text-white/60 text-center w-full leading-tight">{t('imageGeneration.tooltipPrint')}</span>
                                    </button>
                                )}

                                {/* Adjustments */}
                                {!minimalActions && (
                                    <button
                                        type="button"
                                        onClick={() => setShowEditSidebar(s => !s)}
                                        className="flex flex-col items-center gap-1.5 active:opacity-70 transition-opacity w-16"
                                    >
                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${showEditSidebar ? 'bg-teal-100 dark:bg-teal-500/30' : 'bg-zinc-100 dark:bg-white/10'}`}>
                                            <IconWand size={22} className={showEditSidebar ? 'text-teal-600 dark:text-teal-400' : 'text-zinc-600 dark:text-white/80'} />
                                        </div>
                                        <span className="text-[10px] font-medium text-zinc-500 dark:text-white/60 text-center w-full leading-tight">{t('imageGeneration.tooltipAdjustments')}</span>
                                    </button>
                                )}

                                {/* Delete */}
                                {!minimalActions && (
                                    <button
                                        type="button"
                                        onClick={() => setShowDeleteConfirm(true)}
                                        className="flex flex-col items-center gap-1.5 active:opacity-70 transition-opacity w-16"
                                    >
                                        <div className="w-12 h-12 rounded-2xl bg-red-100 dark:bg-red-500/20 flex items-center justify-center">
                                            <IconTrash size={22} className="text-red-500 dark:text-red-400" />
                                        </div>
                                        <span className="text-[10px] font-medium text-zinc-500 dark:text-white/60 text-center w-full leading-tight">{t('imageGeneration.tooltipDelete')}</span>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* ── Adjustments drawer (slides up from bottom) ── */}
                        {showEditSidebar && (
                            <div className="absolute inset-x-0 bottom-0 bg-white dark:bg-zinc-900 rounded-t-3xl border-t border-zinc-200 dark:border-white/10 z-20 max-h-[60vh] flex flex-col shadow-[0_-8px_32px_rgba(0,0,0,0.12)] dark:shadow-none" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
                                <div className="flex items-center justify-between px-5 pt-4 pb-3 flex-shrink-0">
                                    <span className="text-sm font-bold text-zinc-800 dark:text-white">{t('imageGeneration.imageAdjustments')}</span>
                                    <div className="flex items-center gap-2">
                                        {hasAdjustmentsChanged && (
                                            <button
                                                type="button"
                                                onClick={() => { setBrightness(100); setContrast(100); setSaturation(100); setGrayscale(0); setSharp(0); }}
                                                className="text-xs text-orange-500 dark:text-orange-400 font-medium"
                                            >
                                                {t('imageGeneration.resetAdjustments')}
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => setShowEditSidebar(false)}
                                            className="w-7 h-7 rounded-full bg-zinc-100 dark:bg-white/10 flex items-center justify-center text-zinc-600 dark:text-white"
                                        >
                                            <IconX size={14} />
                                        </button>
                                    </div>
                                </div>
                                <div className="overflow-y-auto flex-1 px-5 pb-6 flex flex-col gap-4">
                                    {[
                                        { label: t('imageGeneration.brightness'), value: brightness, setter: setBrightness, min: 50, max: 200, neutral: 100, display: `${brightness >= 100 ? '+' : ''}${brightness - 100}%` },
                                        { label: t('imageGeneration.contrast'), value: contrast, setter: setContrast, min: 50, max: 200, neutral: 100, display: `${contrast >= 100 ? '+' : ''}${contrast - 100}%` },
                                        { label: t('imageGeneration.saturation'), value: saturation, setter: setSaturation, min: 0, max: 200, neutral: 100, display: `${saturation >= 100 ? '+' : ''}${saturation - 100}%` },
                                        { label: t('imageGeneration.sharp'), value: sharp, setter: setSharp, min: 0, max: 10, neutral: 0, step: 1, display: `${sharp * 10}%`, raw: true },
                                    ].map(({ label, value, setter, min, max, neutral, step, display, raw }) => (
                                        <div key={label}>
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wide">{label}</span>
                                                <span className="text-xs font-bold text-orange-500 dark:text-orange-400">{display}</span>
                                            </div>
                                            <input
                                                type="range"
                                                className="w-full accent-orange-500"
                                                min={min} max={max} step={step ?? undefined}
                                                value={value}
                                                onChange={e => raw ? setter(Number(e.target.value)) : withSnap(setter, neutral)(e.target.value)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                /* ══════════════════════════════════════════════════
                    DESKTOP — original layout
                ══════════════════════════════════════════════════ */
                <>
                {/* ── Header toolbar — floats OVER the image (absolute, transparent) so the
                    photo keeps full height. A soft top scrim keeps the controls legible over
                    any image. Single mode hugs the image width (centered above the photo);
                    split compare mode floats over the right (after) pane. Auto-hides and
                    reveals on hover over the image viewer (see chromeVisible). ── */}
                <div
                    onMouseEnter={showChrome}
                    onMouseMove={showChrome}
                    onMouseLeave={hideChrome}
                    className="absolute top-0 z-[60] flex items-center flex-wrap gap-1.5 px-3 py-2.5 bg-gradient-to-b from-black/35 to-transparent"
                    style={{
                        ...headerPosStyle,
                        opacity: chromeVisible ? 1 : 0,
                        transform: chromeVisible ? 'translateY(0)' : 'translateY(-8px)',
                        pointerEvents: chromeVisible ? 'auto' : 'none',
                        transition: 'opacity 0.25s ease, transform 0.25s ease, left 0.2s ease, right 0.2s ease, width 0.2s ease',
                    }}
                >

                    {/* Make-active / select-this-preset button */}
                    {onMakeActive && selectedImage && (
                        <button
                            type="button"
                            onClick={() => onMakeActive(selectedImage.id)}
                            disabled={selectedImage.id === activeId}
                            className={`flex-shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[13px] font-semibold transition-colors ${
                                selectedImage.id === activeId
                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 cursor-default'
                                    : 'bg-primary text-white hover:bg-primary/90'
                            }`}
                        >
                            <IconCheck size={15} stroke={2.4} />
                            {selectedImage.id === activeId ? selectedLabel : makeActiveLabel}
                        </button>
                    )}

                    {/* Action group — left-aligned labelled pills so every control is
                        self-explanatory; wraps on very narrow modals instead of clipping. */}
                    <div className="flex items-center gap-1.5 flex-wrap justify-start">

                        {/* Side-by-side toggle — hidden for AI-template generations, whose
                            result is a composite layout (before/after comparison is meaningless) */}
                        {!minimalActions && selectedImage?.uploadedImage && !selectedImage?.aiTemplateGeneration && (
                            <IconBtn
                                title={viewMode === 'single' ? t('imageGeneration.compareBeforeAfter') : t('imageGeneration.exitCompare')}
                                label={showLabel('compare') ? (viewMode === 'single' ? t('imageGeneration.labelCompare') : t('imageGeneration.labelExit')) : undefined}
                                onClick={() => setViewMode(m => m === 'single' ? 'side-by-side' : 'single')}
                                className={viewMode === 'side-by-side'
                                    ? 'text-white bg-indigo-600 hover:bg-indigo-700'
                                    : 'text-gray-700 bg-gray-200 hover:bg-gray-300'}
                            >
                                {viewMode === 'side-by-side' ? <IconColumns2 size={18} /> : <IconLayoutColumns size={18} />}
                            </IconBtn>
                        )}

                        {/* Share dropdown */}
                        {!hideShare && (
                        <DropdownMenu>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <DropdownMenuTrigger
                                        type="button"
                                        className={`${showLabel('share') ? 'h-8 px-2.5 gap-1.5 text-[13px] font-semibold' : 'p-1.5'} rounded-lg inline-flex items-center justify-center whitespace-nowrap transition-colors text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50`}
                                    >
                                        <IconShare size={18} />
                                        {showLabel('share') && <span>{t('imageGeneration.labelShare')}</span>}
                                    </DropdownMenuTrigger>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">{t('imageGeneration.tooltipShare')}</TooltipContent>
                            </Tooltip>
                            <DropdownMenuContent align="end" className="w-48" onMouseEnter={showChrome} onCloseAutoFocus={hideChrome}>
                                <DropdownMenuLabel>{t('imageGeneration.shareImage')}</DropdownMenuLabel>
                                {navigator.share && (
                                    <>
                                        <DropdownMenuItem onClick={() => handleShare('native')}>
                                            <IconShare size={16} className="mr-2" /> {t('imageGeneration.share')}...
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                    </>
                                )}
                                <DropdownMenuItem onClick={() => handleShare('copy')}>
                                    <IconCopy size={16} className="mr-2" /> {t('imageGeneration.copyImage')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        )}

                        {/* Re-edit */}
                        {!hideEdit && (
                            <IconBtn
                                title={t('imageGeneration.tooltipReopenEditor')}
                                label={showLabel('edit') ? t('imageGeneration.labelEdit') : undefined}
                                onClick={handleEdit}
                                loading={isLoadingEdit}
                                disabled={isLoadingEdit}
                                className="text-white bg-orange-500 hover:bg-orange-600"
                            >
                                <IconSparkles size={18} />
                            </IconBtn>
                        )}

                        {/* Download button / dropdown */}
                        {!hideDownload && (hideCustomDownload ? (
                            <IconBtn
                                title={t('imageGeneration.tooltipDownload')}
                                label={showLabel('download') ? t('imageGeneration.labelDownload') : undefined}
                                onClick={handleDownload}
                                loading={downloading}
                                disabled={downloading}
                                className="text-white bg-emerald-600 hover:bg-emerald-700"
                            >
                                <IconDownload size={18} />
                            </IconBtn>
                        ) : (
                        <DropdownMenu>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <DropdownMenuTrigger
                                        type="button"
                                        disabled={downloading}
                                        className={`${showLabel('download') ? 'h-8 px-2.5 gap-1.5 text-[13px] font-semibold' : 'p-1.5'} rounded-lg inline-flex items-center justify-center whitespace-nowrap transition-colors text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed`}
                                    >
                                        {downloading
                                            ? <svg className="animate-spin h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
                                            : <IconDownload size={18} />
                                        }
                                        {showLabel('download') && <span>{t('imageGeneration.labelDownload')}</span>}
                                    </DropdownMenuTrigger>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">{t('imageGeneration.tooltipDownload')}</TooltipContent>
                            </Tooltip>
                            <DropdownMenuContent align="end" className="w-52" onMouseEnter={showChrome} onCloseAutoFocus={hideChrome}>
                                <DropdownMenuLabel>{t('imageGeneration.downloadOptions')}</DropdownMenuLabel>
                                <DropdownMenuItem className="cursor-pointer" onClick={handleDownload}>
                                    <IconDownload size={16} className="mr-2" /> {t('imageGeneration.downloadOriginal')}
                                </DropdownMenuItem>
                                <DropdownMenuItem className="cursor-pointer" onClick={() => onCustomDownload?.(selectedImage.aiImage, selectedImage.id, selectedImage.background)}>
                                    <IconPlus size={16} className="mr-2" /> {t('imageGeneration.customSizeQuality')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        ))}

                        {/* Print */}
                        {!hidePrint && (
                            <IconBtn
                                title={t('imageGeneration.tooltipPrint')}
                                label={showLabel('print') ? t('imageGeneration.labelPrint') : undefined}
                                onClick={handlePrint}
                                loading={printing}
                                disabled={printing}
                                className="text-white bg-cyan-600 hover:bg-cyan-700"
                            >
                                <IconPrinter size={18} />
                            </IconBtn>
                        )}

                        {/* Delete */}
                        {!minimalActions && !hideDelete && (
                            <IconBtn
                                title={t('imageGeneration.tooltipDelete')}
                                label={showLabel('delete') ? t('imageGeneration.labelDelete') : undefined}
                                onClick={() => setShowDeleteConfirm(true)}
                                className="text-white bg-red-600 hover:bg-red-700"
                            >
                                <IconTrash size={18} />
                            </IconBtn>
                        )}

                        {/* Adjustments */}
                        {!minimalActions && (
                            <IconBtn
                                title={t('imageGeneration.tooltipAdjustments')}
                                label={showLabel('glow') ? t('imageGeneration.labelGlow') : undefined}
                                onClick={() => setShowEditSidebar(s => !s)}
                                className={showEditSidebar
                                    ? 'text-white bg-teal-600 hover:bg-teal-700'
                                    : 'text-white bg-gray-700 hover:bg-gray-800'}
                            >
                                <IconWand size={18} />
                            </IconBtn>
                        )}

                    </div>

                    {/* Close — pinned to the right edge (ml-auto pushes it away from the left-aligned actions) */}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-shrink-0 ml-auto h-8 w-8 rounded-lg flex items-center justify-center bg-red-600 hover:bg-red-700 text-white transition-colors shadow-sm"
                            >
                                <IconX size={16} strokeWidth={3} />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="flex items-center gap-1.5">
                            <span>{t('imageGeneration.tooltipClose')}</span>
                            <kbd className="px-1.5 py-0.5 rounded bg-white/20 text-[10px] font-semibold leading-none">Esc</kbd>
                        </TooltipContent>
                    </Tooltip>
                </div>

                {/* ── Body ───────────────────────────────────────────────────── */}
                <div
                    className="flex overflow-hidden animate-ipmImageReveal"
                    style={{ flexDirection: 'row', flex: 1, minHeight: 0 }}
                >
                    {/* Main images area */}
                    <div
                        className="group"
                        style={{ flex: 1, position: 'relative', overflow: 'hidden' }}
                    >

                        {/* Prev arrow */}
                        {currentIndex > 0 && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        onClick={handlePrevImage}
                                        className="opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-200"
                                        style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', zIndex: 20, backgroundColor: 'rgba(0,0,0,0.6)', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', border: 'none', cursor: 'pointer' }}
                                    >
                                        <IconChevronLeft size={28} />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="right">{t('imageGeneration.tooltipPrev')}</TooltipContent>
                            </Tooltip>
                        )}

                        {/* Next arrow */}
                        {hasNextImage && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        onClick={handleNextImage}
                                        disabled={pendingLoadMoreRef.current && isLoadingMore}
                                        className="opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-200"
                                        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', zIndex: 20, backgroundColor: 'rgba(0,0,0,0.6)', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', border: 'none', cursor: 'pointer' }}
                                    >
                                        {pendingLoadMoreRef.current && isLoadingMore
                                            ? <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
                                            : <IconChevronRight size={28} />
                                        }
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="left">{t('imageGeneration.tooltipNext')}</TooltipContent>
                            </Tooltip>
                        )}

                        {/* ── Side-by-side ── */}
                        {viewMode === 'side-by-side' && selectedImage?.uploadedImage ? (
                            <div style={{ display: 'flex', gap: 0, flexDirection: isDual ? 'column' : 'row', height: '100%', alignItems: 'stretch' }}>

                                {/* Before pane */}
                                <div
                                    ref={beforeDrag.containerRef}
                                    style={{ flex: 1, position: 'relative', overflow: 'hidden', cursor: beforeZoom > 1 ? (beforeDrag.dragRef.current ? 'grabbing' : 'grab') : 'default', userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'none' }}
                                    onMouseEnter={() => { setBeforeHover(true); showChrome(); }}
                                    onMouseMove={showChrome}
                                    onMouseLeave={() => { setBeforeHover(false); beforeDrag.dragRef.current = null; hideChrome(); }}
                                    onPointerDown={beforeDrag.onPointerDown}
                                    onPointerMove={beforeDrag.onPointerMove}
                                    onPointerUp={beforeDrag.onPointerUp}
                                    onPointerCancel={beforeDrag.onPointerUp}
                                >
                                    <img src={selectedImage.uploadedImage} alt="Before" draggable={false}
                                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', objectFit: 'contain', objectPosition: 'center center', transform: `scale(${beforeZoom}) translate(${beforePan.x / beforeZoom}px, ${beforePan.y / beforeZoom}px)`, transformOrigin: 'center center', transition: (beforeDrag.dragRef.current || afterCompareDrag.dragRef.current) ? 'none' : 'transform 0.15s ease', userSelect: 'none', pointerEvents: 'none' }}
                                    />
                                    <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.6)', borderRadius: 6, padding: '3px 10px', zIndex: 2, whiteSpace: 'nowrap', userSelect: 'none', pointerEvents: 'none' }}>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: 'white' }}>{t('imageGeneration.beforeLabel')}</span>
                                    </div>
                                    <ZoomControls zoom={beforeZoom} setZoom={setCompareZoom} setPan={setComparePan} visible={isDual || beforeHover} t={t} />
                                </div>

                                {/* After pane */}
                                <div
                                    ref={afterCompareDrag.containerRef}
                                    style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: afterZoom > 1 ? (afterCompareDrag.dragRef.current ? 'grabbing' : 'grab') : 'default', userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'none' }}
                                    onMouseEnter={() => { setAfterHover(true); showChrome(); }}
                                    onMouseMove={showChrome}
                                    onMouseLeave={() => { setAfterHover(false); afterCompareDrag.dragRef.current = null; hideChrome(); }}
                                    onPointerDown={afterCompareDrag.onPointerDown}
                                    onPointerMove={afterCompareDrag.onPointerMove}
                                    onPointerUp={afterCompareDrag.onPointerUp}
                                    onPointerCancel={afterCompareDrag.onPointerUp}
                                >
                                    {/* Background is on the <img> itself: with auto width/height +
                                        max 100% the img box shrinks to the CONTAINED image, so the colour
                                        fills exactly behind the photo — never the letterbox margins. */}
                                    <img src={selectedImage.aiImage} alt="After" draggable={false}
                                        onLoad={(e) => { const el = e.currentTarget; if (el.naturalHeight) setImgAspectRatio(el.naturalWidth / el.naturalHeight); }}
                                        style={{ display: 'block', maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', filter: cssFilter, transform: `scale(${afterZoom}) translate(${afterPan.x / afterZoom}px, ${afterPan.y / afterZoom}px)`, transformOrigin: 'center center', transition: (afterCompareDrag.dragRef.current || beforeDrag.dragRef.current) ? 'none' : 'transform 0.15s ease', userSelect: 'none', pointerEvents: 'none', ...getImageBgStyle(selectedImage) }}
                                    />
                                    <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.6)', borderRadius: 6, padding: '3px 10px', zIndex: 2, whiteSpace: 'nowrap', userSelect: 'none', pointerEvents: 'none' }}>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: 'white' }}>{t('imageGeneration.afterLabel')}</span>
                                    </div>
                                    <ZoomControls zoom={afterZoom} setZoom={setCompareZoom} setPan={setComparePan} visible={isDual || afterHover} t={t} />
                                    {!showEditSidebar && !hidePrint && (
                                        <div style={{ position: 'absolute', bottom: 12, right: 12, zIndex: 5, opacity: afterHover ? 1 : 0, transition: 'opacity 0.2s ease', pointerEvents: afterHover ? 'auto' : 'none' }} onPointerDown={e => e.stopPropagation()}>
                                            <button type="button" onClick={handlePrint} disabled={printing} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-cyan-600 hover:bg-cyan-700 text-white rounded transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                                                {printing ? <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg> : <IconPrinter size={14} />} {t('common.print') || 'প্রিন্ট'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            /* ── Single ── */
                            <div
                                style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onMouseEnter={() => setAfterHover(true)}
                                onMouseLeave={() => setAfterHover(false)}
                            >
                                {/* Fixed frame at the image's natural fit size. Zooming scales the
                                    photo INSIDE this frame (overflow clipped), so the frame never
                                    grows and the image is cropped/pannable rather than spilling into
                                    the dark margins. For manually-edited (passport-style) images the
                                    chosen background fills this same frame. */}
                                <div
                                    ref={afterDrag.containerRef}
                                    style={{
                                        position: 'relative',
                                        width: singleBaseW,
                                        height: singleBaseH,
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        overflow: 'hidden',
                                        cursor: afterZoom > 1 ? (afterDrag.dragRef.current ? 'grabbing' : 'grab') : 'default',
                                        ...getImageBgStyle(selectedImage),
                                    }}
                                    onMouseEnter={showChrome}
                                    onMouseMove={showChrome}
                                    onMouseLeave={hideChrome}
                                    onPointerDown={afterDrag.onPointerDown}
                                    onPointerMove={afterDrag.onPointerMove}
                                    onPointerUp={afterDrag.onPointerUp}
                                    onPointerCancel={afterDrag.onPointerUp}
                                >
                                    <img
                                        src={selectedImage.aiImage}
                                        alt="Preview"
                                        draggable={false}
                                        onLoad={(e) => {
                                            const el = e.currentTarget;
                                            if (el.naturalHeight) setImgAspectRatio(el.naturalWidth / el.naturalHeight);
                                            if (pendingPanRef.current) {
                                                const pending = pendingPanRef.current;
                                                pendingPanRef.current = null;
                                                requestAnimationFrame(() => {
                                                    if (!el.isConnected) return;
                                                    setAfterPan({
                                                        x: pending.ox * el.clientWidth,
                                                        y: pending.oy * el.clientHeight,
                                                    });
                                                });
                                            }
                                        }}
                                        style={{ width: '100%', height: '100%', objectFit: 'contain', filter: cssFilter, transform: `scale(${afterZoom}) translate(${afterPan.x / afterZoom}px, ${afterPan.y / afterZoom}px)`, transformOrigin: 'center center', transition: afterDrag.dragRef.current ? 'none' : 'transform 0.15s ease', userSelect: 'none', pointerEvents: 'none', display: 'block' }}
                                    />
                                </div>
                                <ZoomControls zoom={afterZoom} setZoom={setAfterZoom} setPan={setAfterPan} visible={afterHover} t={t} />
                                {!showEditSidebar && !hidePrint && (
                                    <div style={{ position: 'absolute', bottom: 10, right: 12, zIndex: 5, opacity: afterHover ? 1 : 0, transition: 'opacity 0.2s ease', pointerEvents: afterHover ? 'auto' : 'none' }} onPointerDown={e => e.stopPropagation()}>
                                        <button type="button" onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-cyan-600 hover:bg-cyan-700 text-white rounded transition-colors">
                                            <IconPrinter size={14} /> {t('common.print') || 'প্রিন্ট'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Counter — hides when zoom controls are visible to avoid overlap */}
                        {(images?.length > 1 || hasMore) && (
                            <div style={{ position: 'absolute', bottom: 10, ...(isDual ? { left: 10 } : { left: '50%', transform: 'translateX(-50%)' }), backgroundColor: 'rgba(0,0,0,0.7)', color: 'white', padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 500, zIndex: 10, opacity: afterHover ? 0 : 1, transition: 'opacity 0.2s ease', pointerEvents: 'none' }}>
                                {currentIndex + 1} / {images?.length ?? 0}
                            </div>
                        )}
                    </div>

                    {/* ── Adjustments sidebar ─────────────────────────────────── */}
                    {showEditSidebar && (
                        <div className="bg-white dark:bg-zinc-900" style={{ width: 260, flexShrink: 0, borderLeft: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: '100vh', alignSelf: 'stretch' }}>
                            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '12px 16px 8px' }}>
                                <div className="flex justify-between items-center mb-4 min-h-7">
                                    <span className="text-sm font-bold">{t('imageGeneration.imageAdjustments')}</span>
                                    {hasAdjustmentsChanged && (
                                        <IconBtn
                                            title={t('imageGeneration.resetAdjustments')}
                                            onClick={() => { setBrightness(100); setContrast(100); setSaturation(100); setGrayscale(0); setSharp(0); }}
                                            className="text-blue-600 bg-blue-50 hover:bg-blue-100"
                                        >
                                            <IconRefresh size={16} />
                                        </IconBtn>
                                    )}
                                </div>
                                <div className="flex flex-col gap-3.5">
                                    {/* Brightness */}
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('imageGeneration.brightness')}</span>
                                            <span className="text-xs font-bold text-orange-500">{brightness >= 100 ? '+' : ''}{brightness - 100}%</span>
                                        </div>
                                        <input type="range" className="w-full accent-orange-500" min={50} max={200} value={brightness} onChange={e => withSnap(setBrightness, 100)(e.target.value)} />
                                    </div>
                                    {/* Contrast */}
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('imageGeneration.contrast')}</span>
                                            <span className="text-xs font-bold text-orange-500">{contrast >= 100 ? '+' : ''}{contrast - 100}%</span>
                                        </div>
                                        <input type="range" className="w-full accent-orange-500" min={50} max={200} value={contrast} onChange={e => withSnap(setContrast, 100)(e.target.value)} />
                                    </div>
                                    {/* Saturation */}
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('imageGeneration.saturation')}</span>
                                            <span className="text-xs font-bold text-orange-500">{saturation >= 100 ? '+' : ''}{saturation - 100}%</span>
                                        </div>
                                        <input type="range" className="w-full accent-orange-500" min={0} max={200} value={saturation} onChange={e => withSnap(setSaturation, 100)(e.target.value)} />
                                    </div>
                                    {/* Sharp */}
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('imageGeneration.sharp')}</span>
                                            <span className="text-xs font-bold text-orange-500">{sharp * 10}%</span>
                                        </div>
                                        <input type="range" className="w-full accent-orange-500" min={0} max={10} step={1} value={sharp} onChange={e => setSharp(Number(e.target.value))} />
                                    </div>
                                </div>
                            </div>
                            {!hidePrint && (
                                <div style={{ flexShrink: 0, padding: '12px 16px', borderTop: '1px solid #e5e7eb' }}>
                                    <button type="button" onClick={handlePrint} disabled={printing} className="w-full flex items-center justify-center gap-2 py-3 text-sm font-medium bg-cyan-600 hover:bg-cyan-700 text-white rounded-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                                        {printing ? <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg> : <IconPrinter size={20} />} {t('common.print') || 'প্রিন্ট'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                </>
                )}
            </DialogContent>
        </Dialog>

        {/* ── Delete confirmation dialog ──────────────────────────────────────── */}
        <ConfirmDialog
            open={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            onConfirm={handleDelete}
            title={t('imageGeneration.deleteImage')}
            description={t('imageGeneration.deleteConfirmation')}
            loading={deleting}
            // This preview sits at z-[99999]; lift the confirmation above it so it
            // isn't hidden behind the fullscreen viewer.
            zClass="z-[100001]"
            overlayZClass="z-[100000]"
        />
        </>
    );
}
